import {Icon} from '@wandb/components';
import {
  Content as DropdownContent,
  Portal as DropdownPortal,
  Root as DropdownRoot,
} from '@wandb/weave/components/DropdownMenu';
import React, {useState} from 'react';

import {useIsInLoadedReportContext} from '../../../pages/ReportPage/LoadedReportContext';
import {
  type RunMenuAction,
  trackRunMenu,
} from '../../../services/analytics/runMenuEvents';
import type {WBTableRowWithRunSetInfo} from '../../../util/tablecols';
import {useRunSelectorFeatureEnabled} from '../RunSelectorFeaturesContext';
import {useRunSelectorProjectInfo} from '../RunSelectorRunSetContext';
import {RenameRunGroupDialog} from './RenameRunGroupDialog';
import {RowHeaderMenuOption} from './RowHeaderMenuOption';
import {RowHeaderMenuTrigger} from './RowHeaderMenuTrigger';

type GroupRowHeaderMenuProps = {
  row: WBTableRowWithRunSetInfo;
  visible: boolean;
};

/**
 * The overflow menu for a run group row, the group-row counterpart to
 * {@link RunRowHeaderMenu}. Only rendered for rows grouped by run group — a row
 * grouped by, say, `config.lr` is not a group anyone can rename.
 *
 * @param props {@link GroupRowHeaderMenuProps}
 */
export function GroupRowHeaderMenu(props: GroupRowHeaderMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const isReport = useIsInLoadedReportContext();
  const isEnabled = useRunSelectorFeatureEnabled();
  const {entityName, projectName} = useRunSelectorProjectInfo();

  const groupName = props.row.group;

  if (!isEnabled('edit-run-name') || groupName == null || groupName === '') {
    return null;
  }

  const trackRunMenuWithLocation = (action: RunMenuAction) => {
    trackRunMenu({action, location: isReport ? 'report' : 'workspace'});
  };

  return (
    <>
      <DropdownRoot open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
        <RowHeaderMenuTrigger
          ariaLabel="Show run group menu"
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
          visible={menuOpen || props.visible}
        />
        <DropdownPortal>
          <DropdownContent align="start" sideOffset={8} alignOffset={-8}>
            <RowHeaderMenuOption
              data-dd-action-name="Edit group name"
              icon={<Icon icon="pencil-edit" />}
              label="Edit group name"
              onSelect={() => {
                setIsRenameDialogOpen(true);
                trackRunMenuWithLocation('Edit group name');
              }}
            />
          </DropdownContent>
        </DropdownPortal>
      </DropdownRoot>
      {isRenameDialogOpen && (
        <RenameRunGroupDialog
          closeDialog={() => setIsRenameDialogOpen(false)}
          entityName={entityName}
          groupName={groupName}
          projectName={projectName}
        />
      )}
    </>
  );
}
